import type { FeatureData } from "@/types/spectrum";

/**
 * TIC 관찰 rug 트랙 — 픽셀 겹침 기준 클러스터링.
 *
 * 2026-09-03 (외부 자문 반영): 종전에는 관찰마다 수직선을 그렸다. 중복제거
 * 해제로 관찰이 170건이 되자 선 340개(투명 히트영역 포함)가 차트를 덮었고,
 * 20px 히트영역이 ~6px 간격으로 겹쳐 "가장 가까운 관찰"이 아니라 나중에
 * 그려진 선이 선택됐다.
 *
 * 상위 N개만 그리는 방식은 "나머지는 어디 갔나"를 만든다. 대신 **전부 그리되
 * 화면에서 겹치는 것을 묶어** 개수를 적는다 — 확대하면 저절로 분해된다.
 * 숨기는 것이 없으므로 "상위 N + 외 N건" 같은 임의 기준이 필요 없다.
 */

/** 이 픽셀 거리 안의 관찰은 한 눈금으로 묶는다(눈으로 구분 불가한 간격). */
export const RUG_CLUSTER_GAP_PX = 6;

export interface RugObservation {
  /** 관찰 식별자 — 화면 표시 번호가 아니라 안정 식별자. */
  id: string;
  rt: number;
  features: FeatureData[];
}

export interface RugCluster {
  /** 묶인 관찰들 — RT 오름차순. */
  observations: RugObservation[];
  /** 묶음의 대표 RT(첫 관찰) — 눈금을 그릴 위치. */
  rt: number;
  /** 묶음의 화면 x 좌표(px). */
  x: number;
  /** 묶인 관찰 수. 1이면 단일 눈금. */
  size: number;
}

/**
 * 관찰 목록 → 화면 클러스터. `toPixel` 은 RT → 캔버스 x(px) 변환이다.
 *
 * 보이는 범위 밖(음수·폭 초과)은 제외한다 — 확대 중에는 그 구간만 그린다.
 * 같은 클러스터의 관찰은 전부 보존되므로 클릭 시 개별 선택이 가능하다.
 */
export function clusterObservationsForRug(
  observations: RugObservation[],
  toPixel: (rt: number) => number,
  viewportWidth: number,
  gapPx: number = RUG_CLUSTER_GAP_PX,
): RugCluster[] {
  const visible = observations
    .map((observation) => ({ observation, x: toPixel(observation.rt) }))
    .filter(
      ({ x }) => Number.isFinite(x) && x >= 0 && x <= viewportWidth,
    )
    .sort((a, b) => a.x - b.x);

  const clusters: RugCluster[] = [];
  for (const { observation, x } of visible) {
    const last = clusters[clusters.length - 1];
    if (last && x - last.x <= gapPx) {
      last.observations.push(observation);
      last.size += 1;
      continue;
    }
    clusters.push({
      observations: [observation],
      rt: observation.rt,
      x,
      size: 1,
    });
  }
  return clusters;
}

/**
 * 클릭 지점(px)에서 허용오차 안의 가장 가까운 관찰.
 *
 * 클러스터로 묶였든 아니든 **모든 관찰**이 대상이다 — 화면에서 눈금 하나로
 * 보여도 그 안의 관찰을 고를 수 있어야 한다(확대하면 분해된다).
 */
export function nearestRugObservation(
  observations: RugObservation[],
  toPixel: (rt: number) => number,
  clickX: number,
  tolerancePx: number,
): RugObservation | null {
  let best: RugObservation | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const observation of observations) {
    const distance = Math.abs(toPixel(observation.rt) - clickX);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = observation;
    }
  }
  return best !== null && bestDistance <= tolerancePx ? best : null;
}
